/**
 * Leitura das variaveis de ambiente do cliente.
 *
 * Existe para que a decisao "usar mocks ou nao" seja tomada em UM lugar. Antes
 * estava repetida em `installMocks` e no `AuthProvider`, e bastava esquecer o
 * arquivo `.env` para o app pedir `/api/v1/bootstrap` a um backend inexistente
 * e morrer com um 500 do proxy do Vite — sem dizer o que fazer.
 */

/**
 * Leitura tolerante de `import.meta.env`.
 *
 * Dentro do Vite ele sempre existe. Fora — nos scripts de verificacao, que
 * importam este codigo com tsx — nao existe, e ler uma propriedade dele
 * derruba o script inteiro no import. O objeto vazio faz cada leitura cair no
 * seu proprio padrao, que e o comportamento correto para quem roda fora do
 * navegador.
 *
 * O que NAO passa por aqui e o `import.meta.env.DEV` de `main.tsx`: aquele
 * precisa continuar literal para o Rollup dobrar a condicao e deixar os mocks
 * de fora do bundle de producao.
 */
const env: Partial<ImportMetaEnv & { DEV: boolean }> =
  (import.meta as { env?: ImportMetaEnv & { DEV: boolean } }).env ?? {};

/**
 * Mocks em desenvolvimento: LIGADOS por padrao.
 *
 * Quem clona o repositorio e roda `npm run dev` nao tem backend nenhum de pe;
 * o padrao precisa ser o caminho que funciona. Para falar com o backend real,
 * declare `VITE_USE_MOCKS=false` no `.env`.
 *
 * Em build de producao `import.meta.env.DEV` e falso, entao os mocks nunca
 * sobem, independentemente do que a variavel diga.
 */
export const usesMocks: boolean = (() => {
  const declared = env.VITE_USE_MOCKS;

  if (declared === 'true') return env.DEV ?? false;
  if (declared === 'false') return false;

  return env.DEV ?? false;
})();

export const apiBaseUrl: string = env.VITE_API_BASE_URL ?? '/api/v1';

/**
 * URL do servico de pagamentos em C#.
 *
 * Quando definida, as chamadas de cobranca vao para a API REAL, mesmo com os
 * demais mocks ligados. E o que permite integrar um servico de cada vez, em
 * vez de esperar o backend inteiro para testar qualquer coisa.
 *
 * O interceptador de mock so captura chamadas para a MESMA ORIGEM do portal.
 * Uma URL absoluta para outra porta passa direto — condicao que precisou ser
 * corrigida: comparar so o caminho fazia o mock sequestrar as duas APIs reais,
 * que tambem servem em `/api/v1`.
 */
export const paymentsApiUrl: string | null = env.VITE_PAYMENTS_API_URL || null;

/**
 * URL do servico de acervo em C# — anuncios, vitrine e moderacao.
 *
 * Mesma logica do de pagamentos: quando definida, as chamadas de anuncio vao
 * para a API REAL mesmo com os demais mocks ligados. E o que permite integrar
 * um servico de cada vez, em vez de esperar o backend inteiro.
 */
export const platformApiUrl: string | null = env.VITE_PLATFORM_API_URL || null;

/**
 * URL do servico de identidade.
 *
 * Cobre APENAS registro e login. Recuperacao de senha e confirmacao de e-mail
 * continuam no mock — apontar tudo de uma vez faria os fluxos que ainda nao
 * existem falharem com 404 em vez de funcionarem simulados.
 */
export const identityApiUrl: string | null = env.VITE_IDENTITY_API_URL || null;

/**
 * Chave PUBLICA do provedor de pagamento.
 *
 * Vai para o navegador de proposito: e ela que permite o SDK do provedor
 * tokenizar o cartao ANTES de o numero sair da maquina do cliente. O nosso
 * servidor recebe so o token, e nunca ve o numero — que e o que mantem o
 * projeto fora do escopo mais pesado do PCI-DSS.
 *
 * A chave PRIVADA nunca entra aqui. Qualquer `VITE_` vira texto no pacote
 * publicado, visivel para quem abrir o inspetor.
 *
 * Nome NEUTRO de proposito, como `paymentsApiUrl`: o provedor e adaptador, e
 * trocar de Mercado Pago para outro nao pode obrigar a renomear configuracao
 * espalhada pelo portal.
 */
export const paymentPublicKey: string | null = env.VITE_PAYMENT_PUBLIC_KEY || null;

/**
 * Chave de desenvolvimento usada pelo mock de identidade para assinar o JWT
 * que a API de pagamentos valida. Precisa bater com `Jwt:SigningKey` do
 * servico.
 *
 * Em producao quem assina e o servidor de identidade — a chave nunca chega ao
 * navegador.
 */
export const devJwt = {
  secret: env.VITE_DEV_JWT_SECRET ?? '',
  issuer: env.VITE_DEV_JWT_ISSUER ?? 'https://auth.solote.com.br',
  audience: env.VITE_DEV_JWT_AUDIENCE ?? 'solote-payments',
};
